import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import Moment from "moment";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { Link, useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import useRequest from "../../hooks/useRequest";
import Pagination from "../Pagination/Pagination";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import Table from "../Table/Table";
import { SearchInput, SearchSubmitButton } from "../Form/Form";
import { addOneToDate } from "../../util/fn";

const apiName = "brand-model/all";
const titleSingular = "Year";
const titlePlural = "Manufacturing Year";

const OBJ_TABLE = {
  Year: "year",
  // "Created On": "createdAt",
  // status: "isActive",
};

const ViewAll = (props) => {
  const { brandId, modelId } = props.match.params;
  const history = useHistory();
  const location = useLocation();
  const versionId = new URLSearchParams(location.search).get("versionId");

  const searchQueryHandler = (
    page,
    per_page,
    sortBy,
    order,
    year = "",
    isActive = "",
    dateFrom = "1970-01-01",
    dateTo
  ) => {
    if (sortBy.length > 0) {
      if (sortBy == "registered on") {
        sortBy = "createdAt";
      } else if (sortBy === "phone number") {
        sortBy = "contact";
      } else if (sortBy === "status") {
        sortBy = "isActive";
      }
    } else {
      sortBy = "createdAt";
    }
    order = order?.length > 0 ? order : "desc";

    dateFrom = dateFrom.length > 0 ? dateFrom : "1970-01-01";

    dateTo = dateTo ? addOneToDate(new Date(dateTo)) : addOneToDate(new Date());

    const vid = versionId
      ? `&versionId=${encodeURIComponent(versionId)}`
      : "";
    return `${apiName}?page=${page}&per_page=${per_page}&sortBy=${sortBy}&isActive=${isActive}&year=${year}&dateFrom=${dateFrom}&dateTo=${dateTo}&brandId=${brandId}&modelId=${modelId}${vid}`;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    resetField,
    getValues,
    clearErrors,
    setError,
  } = useForm();

  const MySwal = withReactContent(Swal);

  const { records_per_page } = useSelector((state) => state.setting);
  const { roleId, permission } = useSelector((state) => state.auth);

  const [dataList, setDataList] = useState([]);
  const [page, setPage] = useState(1);
  const [totalDocuments, setTotalDocuments] = useState(10);
  const [perPage, setPerPage] = useState(0);
  const [currentSort, setCurrentSort] = useState({
    sortBy: "registered on",
    order: "desc",
  });

  const { request, response } = useRequest();
  const { request: requestChangeStatus, response: responseChangeStatus } =
    useRequest();
  const { request: requestDelete, response: responseDelete } = useRequest();

  useEffect(() => {
    if (!versionId) {
      toast.error("Open manufacturing years from a specific car version.");
      history.replace(`/brands/${brandId}/models/car-version/${modelId}`);
      return;
    }
    if (records_per_page) {
      setPerPage(records_per_page);
      request(
        "GET",
        searchQueryHandler(
          1,
          records_per_page,
          currentSort.sortBy,
          currentSort.order
        )
      );
    }
    document.title = `${titlePlural} - Easyspare`;
  }, [records_per_page, versionId, brandId, modelId]);

  useEffect(() => {
    if (response) {
      // if (response?.data?.manufactureYear) {
        setDataList(response.data?.manufactureYear);
        setTotalDocuments((prev) => response.totalDocuments ?? prev);
      // }
    }
  }, [response]);

  useEffect(() => {
    if (responseChangeStatus) {
      const { id, status } = responseChangeStatus;
      const newDataList = [...dataList];
      const indexToChange = newDataList.findIndex((list) => list._id == id);
      newDataList[indexToChange].isActive = status;
      toast.success(responseChangeStatus.message);
      setDataList(newDataList);
    }
  }, [responseChangeStatus]);

  useEffect(() => {
    if (responseDelete) {
      const { id } = responseDelete;
      let newDataList = [...dataList];
      newDataList = newDataList.filter((list) => list._id != id);
      setDataList(newDataList);
      toast.success(responseDelete.message);
    }
  }, [responseDelete]);

  const fetchMoreData = ({ selected }) => {
    setDataList([]);
    const { isActive, year, dateFrom, dateTo } = getValues();

    setPage(selected + 1);
    request(
      "GET",
      searchQueryHandler(
        selected + 1,
        perPage,
        currentSort.sortBy,
        isActive,
        year,
        dateFrom,
        dateTo
      )
    );
  };

  const deleteHandler = (_id, row) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You want to delete this Year?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        requestDelete("DELETE", `${apiName}`, {
          modelId,
          versionId,
          year: row?.year != null ? row.year : Number(_id),
          yearId: _id,
        });
      } else if (result.isDismissed) {
      }
    });
  };

  const onSearchHandler = (data) => {
    const { isActive, year, dateFrom, dateTo } = getValues();

    if (dateFrom && dateTo) {
      if (Moment(dateFrom).isAfter(dateTo)) {
        setError("dateTo", {
          type: "manual",
        });
        return;
      }
    }
    request(
      "GET",
      searchQueryHandler(
        1,
        perPage,
        currentSort.sortBy,
        isActive,
        year,
        dateFrom,
        dateTo
      )
    );
    setPage(1);
  };

  const onResetHandler = (e) => {
    e.preventDefault();
    resetField("isActive");
    resetField("year");
    resetField("dateFrom");
    resetField("dateTo");
    request(
      "GET",
      searchQueryHandler(1, perPage, currentSort.sortBy, currentSort.order)
    );
    setPage(1);
  };

  const perPageChangeHandler = (event) => {
    const { isActive, year, dateFrom, dateTo } = getValues();

    request(
      "GET",
      searchQueryHandler(
        1,
        event.target.value,
        currentSort.sortBy,
        currentSort.order,
        isActive,
        year,
        dateFrom,
        dateTo
      )
    );
    setPage(1);

    setPerPage(event.target.value);
  };

  const sortingHandler = (sortBy) => {
    const { isActive, year, dateFrom, dateTo } = getValues();

    if (currentSort.sortBy == sortBy) {
      const newOrder = currentSort.order === "asc" ? "desc" : "asc";
      request(
        "GET",
        searchQueryHandler(
          page,
          perPage,
          sortBy,
          newOrder,
          isActive,
          year,
          dateTo
        )
      );
      setCurrentSort({ sortBy, order: newOrder });
    } else {
      request(
        "GET",
        searchQueryHandler(
          page,
          perPage,
          sortBy,
          "desc",
          isActive,
          year,
          dateFrom,
          dateTo
        )
      );
      setCurrentSort({ sortBy, order: "desc" });
    }
  };

  const changeStatusHandler = (id) => {
    const data = dataList.find((list) => list._id == id);
    const status = !data.isActive;
    MySwal.fire({
      title: "Are you sure?",
      text: "Want to change this status?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        requestChangeStatus("PUT", `${apiName}/status`, {
          id,
          status,
        });
      } else if (result.isDismissed) {
      }
    });
  };

  const InputFields = [
    {
      label: "Name",
      name: "year",
      required: false,
    },
    // {
    //   isSelectInput: true,
    //   label: "Status",
    //   name: "isActive",
    //   required: false,
    //   children: (
    //     <>
    //       <option value="">{"Select an option"}</option>
    //       <option value={true}> {"Activated"}</option>
    //       <option value={false}>{"Deactivated"}</option>
    //     </>
    //   ),
    // },
    {
      label: "Date From",
      name: "dateFrom",
      isDate: true,
      clearErrors,
    },
    {
      label: "Date To",
      name: "dateTo",
      isDate: true,
      clearErrors,
      otherRegisterFields: {
        manual: true,
        feedback: "'To Date' cannot be smaller than 'From Date'",
      },
    },
  ];

  if (!versionId) {
    return null;
  }

  return (
    <div
      className="content  d-flex flex-column flex-column-fluid"
      id="kt_content"
    >
      <Breadcrumb
        title={titlePlural}
        links={[
          { to: "/", name: "Dashboard" },
          {
            to: `/brands/${brandId}/models/car-version/${modelId}`,
            name: "Back to car versions",
          },
        ]}
      />

      <div className="d-flex flex-column-fluid">
        <div className=" container ">
          <div className="row">
            <div className="col-12">
              <div className="card card-custom card-stretch card-shadowless">
                <div className="card-header">
                  <div className="card-title"></div>
                  <div className="card-toolbar">
                    <a
                      className="btn btn-primary dropdown-toggle mr-2"
                      data-toggle="collapse"
                      data-target="#collapseOne6"
                    >
                      Search
                    </a>

                    {((roleId === 2 && !!permission["5_22"]) ||
                      roleId == 1) && (
                      <Link
                        to={`/brands/${brandId}/models/car-version/${modelId}/years/add?versionId=${encodeURIComponent(
                          versionId
                        )}`}
                        className="btn btn-primary"
                      >
                        Add New {titleSingular}
                      </Link>
                    )}
                  </div>
                </div>
                <div className="card-body">
                  <div
                    className="accordion accordion-solid accordion-toggle-plus"
                    id="accordionExample6"
                  >
                    <div
                      id="collapseOne6"
                      className="collapse"
                      data-parent="#accordionExample6"
                    >
                      <div>
                        <form
                          onSubmit={handleSubmit(onSearchHandler)}
                          className="kt-form kt-form--fit mb-0"
                        >
                          <div className="row mb-6">
                            {InputFields.map((inputMain, index) => (
                              <SearchInput
                                key={index}
                                {...inputMain}
                                errors={errors}
                                register={register}
                              />
                            ))}
                          </div>

                          <SearchSubmitButton
                            handleSubmit={handleSubmit}
                            onSearchHandler={onSearchHandler}
                            onResetHandler={onResetHandler}
                          />
                        </form>
                        <hr />
                      </div>
                    </div>
                  </div>
                  <div className="dataTables_wrapper ">
                    <Table
                      currentSort={currentSort}
                      sortingHandler={sortingHandler}
                      mainData={dataList}
                      tableHeading={Object.keys(OBJ_TABLE)}
                      tableData={Object.values(OBJ_TABLE)}
                      links={[
                        {
                          isLink: true,
                          to: `/brands/${brandId}/models/car-version/${modelId}/years/edit`,
                          name: "Edit",
                          extraData: true,
                          getTo: (data) => ({
                            pathname: `/brands/${brandId}/models/car-version/${modelId}/years/edit/${data._id}`,
                            search: versionId
                              ? `?versionId=${encodeURIComponent(versionId)}`
                              : "",
                          }),
                          key: "5_24",
                        },
                        {
                          isLink: false,
                          name: "Delete",
                          click: deleteHandler,
                          key: "5_26",
                        },
                        // {
                        //   isLink: true,
                        //   to: `/brands/${id}/models`,
                        //   name: "Year",
                        //   extraData: true,
                        //   title: "ManufacturingYear",
                        //   key: "5_17",
                        // },
                        // {
                        //   isLink: true,
                        //   // to: "/brands",
                        //   name: "Version",
                        //   extraData: true,
                        //   title: "Version",
                        //   key: "5_18",
                        // },
                      ]}
                      onlyDate={{
                        createdAt: "date",
                        startDate: "dateTime",
                        endDate: "dateTime",
                      }}
                    />

                    {perPage !== 0 && (
                      <Pagination
                        page={page}
                        totalDocuments={totalDocuments}
                        getNewData={fetchMoreData}
                        perPage={perPage}
                        defaultPerPage={records_per_page}
                        perPageChangeHandler={perPageChangeHandler}
                        currentDocLength={dataList?.length}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewAll;
